<div class="explorer">

  <div class="explorer-toolbar">
    <button type="button"
            class="nav-button"
            [attr.aria-label]="'actions.back' | translate"
            [title]="'actions.back' | translate"
            [disabled]="!canGoUp() && !exitable()"
            (click)="goUp()">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5 5 12l1.4 1.4L11 8.8V19h2V8.8l4.6 4.6L19 12z"/>
      </svg>
    </button>

    <nav class="breadcrumb" [attr.aria-label]="'media.explorer.path' | translate">
      @for (crumb of breadcrumb(); track crumb.path; let last = $last) {
        <button type="button"
                class="crumb"
                [class.current]="last"
                [disabled]="last"
                (click)="navigate(crumb.path)">{{ crumb.name }}</button>
        @if (!last) {
          <span class="separator" aria-hidden="true">›</span>
        }
      }
    </nav>
  </div>

  <div class="explorer-body">
    @if (loading()) {
      <div class="explorer-message">{{ 'media.explorer.loading' | translate }}</div>
    } @else if (failed()) {
      <div class="explorer-message">
        <span>{{ 'media.loadError' | translate }}</span>
        <button type="button" class="retry" (click)="reload()">{{ 'actions.retry' | translate }}</button>
      </div>
    } @else if (!entries().length) {
      <div class="explorer-message">{{ 'media.folderEmpty' | translate }}</div>
    } @else {
      <div class="entries">
        @for (entry of entries(); track entry.path) {
          @let icon = iconOf(entry);
          <!-- Not a <button>: DownloadService appends a temporary <a download>
               to this element, which may not be nested inside a button. -->
          <div class="entry"
               role="button"
               tabindex="0"
               [class.directory]="entry.kind === 'directory'"
               [title]="entry.name"
               (click)="onEntryClick(entry, $event)"
               (keydown.enter)="onEntryKeydown(entry, $event)"
               (keydown.space)="onEntryKeydown(entry, $event)">
            <span class="entry-icon">
              @if (entry.thumbnail && !thumbnailFailed(entry)) {
                <!-- Server-generated preview (images and the first PDF page).
                     If it cannot be made, the server answers 404 and the tile
                     falls back to the type icon instead of a broken image. -->
                <img class="thumbnail"
                     [src]="entry.thumbnail | serverAuthUrl"
                     [alt]="entry.name"
                     loading="lazy"
                     draggable="false"
                     (error)="onThumbnailError(entry)"/>
                @if (icon !== 'image' && labelOf(entry)) {
                  <span class="ext-badge" [ngClass]="icon">{{ labelOf(entry) }}</span>
                }
              } @else {
                <ng-container [ngSwitch]="icon">
                  <svg *ngSwitchCase="'folder'" class="glyph folder" viewBox="0 0 48 48" aria-hidden="true">
                    <path d="M4 12a3 3 0 0 1 3-3h11l4 4h19a3 3 0 0 1 3 3v3H4z"/>
                    <path class="shade" d="M4 17h40v19a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z"/>
                  </svg>
                  <ng-container *ngSwitchDefault>
                    <svg class="glyph sheet" [ngClass]="icon" viewBox="0 0 48 48" aria-hidden="true">
                      <path class="page" d="M10 4h19l9 9v31a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z"/>
                      <path class="fold" d="M29 4l9 9h-9z"/>
                    </svg>
                    @if (labelOf(entry)) {
                      <span class="ext-badge" [ngClass]="icon">{{ labelOf(entry) }}</span>
                    }
                  </ng-container>
                </ng-container>
              }
              <span class="spinner" aria-hidden="true"></span>
            </span>
            @if (entry.kind === 'file' && entry.src) {
              <!-- Direct download without opening the viewer - the viewer shows
                   images and PDFs, and a member wants the file itself as often
                   as a look at it. Its click must not also open the entry. -->
              <button type="button"
                      class="entry-download"
                      [title]="'media.explorer.download' | translate"
                      [attr.aria-label]="'media.explorer.download' | translate"
                      (click)="onEntryDownload(entry, $event)">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 16 7 11l1.4-1.4 2.6 2.6V3h2v9.2l2.6-2.6L17 11zM5 20v-2h14v2z"/>
                </svg>
              </button>
            }
            <span class="entry-name">{{ entry.name }}</span>
            <span class="entry-meta">
              @if (entry.kind === 'directory') {
                {{ 'media.explorer.folder' | translate }}
              } @else {
                {{ formatSize(entry.size) }}
              }
            </span>
            @if (entryActions(); as actions) {
              <!-- Host-supplied controls (admin rename/delete). Clicks and keys
                   stop here so they never also open the entry, and so typing a
                   space into an inline rename field is not an activation. -->
              <span class="entry-actions"
                    (click)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()">
                <ng-container *ngTemplateOutlet="actions; context: { $implicit: entry, entry: entry }"></ng-container>
              </span>
            }
          </div>
        }
      </div>
    }
  </div>

  <div class="explorer-status">
    @if (!loading() && !failed()) {
      {{ 'media.explorer.items' | translate: { count: entries().length } }}
    }
  </div>
</div>
